'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { SegmentedControl, type Segment } from '../../components/ui/SegmentedControl';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import type { UpgradeTargetsFilters, UpgradeTargetsOrder } from '../../queries/useUpgradeQueries';
import translations from './UpgradeScreen.translations';

const w = (ids: string[]): Segment<string>[] => ids.map((id) => ({ id, label: id }));

interface CategoryDef {
  labelKey: keyof typeof translations.en;
  category?: string;
  type?: string;
  weapons?: Segment<string>[];
}

const CATEGORY_DEFS: Record<string, CategoryDef> = {
  rifle: {
    labelKey: 'categoryRifle',
    category: 'Rifles',
    weapons: w(['AK-47', 'M4A4', 'M4A1-S', 'AWP', 'SSG 08', 'SCAR-20', 'G3SG1', 'FAMAS', 'Galil AR', 'AUG', 'SG 553']),
  },
  pistol: {
    labelKey: 'categoryPistol',
    category: 'Pistols',
    weapons: w(['Glock-18', 'USP-S', 'P2000', 'Desert Eagle', 'Five-SeveN', 'Tec-9', 'P250', 'CZ75-Auto', 'Dual Berettas', 'R8 Revolver']),
  },
  smg: {
    labelKey: 'categorySmg',
    category: 'SMGs',
    weapons: w(['MAC-10', 'MP9', 'MP7', 'UMP-45', 'P90', 'PP-Bizon', 'MP5-SD']),
  },
  heavy: {
    labelKey: 'categoryHeavy',
    category: 'Heavy',
    weapons: w(['Nova', 'XM1014', 'Sawed-Off', 'MAG-7', 'M249', 'Negev']),
  },
  knives: {
    labelKey: 'categoryKnives',
    category: 'Knives',
    weapons: w([
      'Bayonet', 'Bowie Knife', 'Butterfly Knife', 'Classic Knife', 'Falchion Knife', 'Flip Knife', 'Gut Knife',
      'Huntsman Knife', 'Karambit', 'Kukri Knife', 'M9 Bayonet', 'Navaja Knife', 'Nomad Knife', 'Paracord Knife',
      'Shadow Daggers', 'Skeleton Knife', 'Stiletto Knife', 'Survival Knife', 'Talon Knife', 'Ursus Knife',
    ]),
  },
  gloves: {
    labelKey: 'categoryGloves',
    category: 'Gloves',
    weapons: w(['Bloodhound Gloves', 'Broken Fang Gloves', 'Driver Gloves', 'Hand Wraps', 'Hydra Gloves', 'Moto Gloves', 'Specialist Gloves', 'Sport Gloves']),
  },
  equipment: { labelKey: 'categoryEquipment', category: 'Equipment', weapons: w(['Zeus x27']) },
  agents: { labelKey: 'categoryAgents', type: 'agent' },
  stickers: { labelKey: 'categoryStickers', type: 'sticker' },
  patches: { labelKey: 'categoryPatches', type: 'patch' },
  musicKits: { labelKey: 'categoryMusicKits', type: 'music_kit' },
  keychains: { labelKey: 'categoryKeychains', type: 'keychain' },
  graffiti: { labelKey: 'categoryGraffiti', type: 'graffiti' },
};

const CATEGORY_ORDER = Object.keys(CATEGORY_DEFS);
const ALL = 'all';

type Risk = 'all' | 'safe' | 'medium' | 'risky';

const RISK_WINDOW: Record<Risk, Pick<UpgradeTargetsFilters, 'minProbability' | 'maxProbability'>> = {
  all: {},
  safe: { minProbability: 0.45 },
  medium: { minProbability: 0.2, maxProbability: 0.45 },
  risky: { maxProbability: 0.2 },
};

const SEARCH_DEBOUNCE_MS = 300;

export type UpgradeTargetQueryFilters = UpgradeTargetsFilters;

export function useUpgradeTargetFilters(): {
  headerNode: React.ReactNode;
  filtersNode: React.ReactNode;
  queryFilters: UpgradeTargetQueryFilters;
} {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const [activeCategory, setActiveCategory] = useState(ALL);
  const [activeWeapon, setActiveWeapon] = useState(ALL);
  const [risk, setRisk] = useState<Risk>('all');
  const [order, setOrder] = useState<UpgradeTargetsOrder>('chance');
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const weapons = activeCategory === ALL ? undefined : CATEGORY_DEFS[activeCategory]?.weapons;

  useEffect(() => {
    setActiveWeapon(ALL);
  }, [activeCategory]);

  useEffect(() => {
    const term = search.trim();
    const id = setTimeout(() => setSearchTerm(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const queryFilters = useMemo<UpgradeTargetQueryFilters>(() => {
    const def = activeCategory === ALL ? undefined : CATEGORY_DEFS[activeCategory];
    const taxonomy: UpgradeTargetQueryFilters = !def
      ? {}
      : def.category
        ? { category: def.category, type: 'skin', ...(activeWeapon !== ALL ? { weapon: activeWeapon } : {}) }
        : { type: def.type };
    return { ...taxonomy, ...RISK_WINDOW[risk], order, searchTerm };
  }, [activeCategory, activeWeapon, risk, order, searchTerm]);

  const categoryTabs: Segment<string>[] = useMemo(
    () => [
      { id: ALL, label: t('categoryAll') },
      ...CATEGORY_ORDER.flatMap((id) => {
        const def = CATEGORY_DEFS[id];
        return def ? [{ id, label: t(def.labelKey) }] : [];
      }),
    ],
    [t],
  );

  const weaponTabs: Segment<string>[] | undefined = useMemo(
    () => (weapons ? [{ id: ALL, label: t('categoryAll') }, ...weapons] : undefined),
    [weapons, t],
  );

  const riskTabs: Segment<Risk>[] = useMemo(
    () => [
      { id: 'all', label: t('riskAll') },
      { id: 'safe', label: t('riskSafe') },
      { id: 'medium', label: t('riskMedium') },
      { id: 'risky', label: t('riskRisky') },
    ],
    [t],
  );

  const orderTabs: Segment<UpgradeTargetsOrder>[] = useMemo(
    () => [
      { id: 'chance', label: t('orderChance') },
      { id: 'multiplier', label: t('orderMultiplier') },
    ],
    [t],
  );

  const searchInput = (
    <Input
      size="s"
      type="search"
      inputMode="search"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={t('searchTargets')}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      aria-label={t('searchTargets')}
      data-testid="upgrade-target-search"
      className="h-[32px] min-w-0 flex-1 rounded-full"
    />
  );

  const selectClass = 'h-8 rounded-full border-white/10 bg-white/[0.06] py-0 text-[13px]';
  const selectContainerClass = 'w-auto max-w-[180px] shrink-0';

  if (isDesktop) {
    return {
      headerNode: <SegmentedControl segments={riskTabs} value={risk} onChange={setRisk} className="w-auto shrink-0" />,
      filtersNode: (
        <div className="flex flex-wrap items-center gap-2 px-4 pb-1">
          {searchInput}
          <Select
            size="s"
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            aria-label={t('categoryAllItems')}
            data-testid="upgrade-target-category"
            className={selectClass}
            containerClassName={selectContainerClass}
          >
            {categoryTabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.id === ALL ? t('categoryAllItems') : tab.label}
              </option>
            ))}
          </Select>
          {weaponTabs && (
            <Select
              size="s"
              value={activeWeapon}
              onChange={(e) => setActiveWeapon(e.target.value)}
              data-testid="upgrade-target-weapon"
              className={selectClass}
              containerClassName={selectContainerClass}
            >
              {weaponTabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            size="s"
            value={order}
            onChange={(e) => setOrder(e.target.value === 'multiplier' ? 'multiplier' : 'chance')}
            data-testid="upgrade-target-order"
            className={selectClass}
            containerClassName={selectContainerClass}
          >
            <option value="chance">{t('orderCheapFirst')}</option>
            <option value="multiplier">{t('orderPriceyFirst')}</option>
          </Select>
        </div>
      ),
      queryFilters,
    };
  }

  const filtersNode = (
    <>
      <div className="flex items-center gap-2 px-4 pt-2">
        {searchInput}
        <SegmentedControl segments={orderTabs} value={order} onChange={setOrder} className="w-auto shrink-0" />
      </div>
      <SegmentedControl segments={riskTabs} value={risk} onChange={setRisk} className="px-4 py-2" />
      <SegmentedControl segments={categoryTabs} value={activeCategory} onChange={setActiveCategory} className="px-4 pb-2" />
      {weaponTabs && (
        <SegmentedControl segments={weaponTabs} value={activeWeapon} onChange={setActiveWeapon} className="px-4 pb-2" />
      )}
    </>
  );

  return { headerNode: null, filtersNode, queryFilters };
}
